// InvScanKit.jsx — scan behaviour shared by Add Inventory, Cycle Count and Stock Take, the way the Picking Portal does it:
//   useScanAlert     an invalid scan flashes the screen red, buzzes and puts up a full-screen notice that has to be
//                    tapped away; nothing can be scanned until it is (so a miss is never scrolled past)
//   useScannerCapture scanner keystrokes are caught at the page level (no field needs the cursor, so no on-screen
//                    keyboard / accessory bar opens over the bottom buttons) — the same trick as the Picking Portal
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { playError, preloadSounds } from './sounds';

export function useScanAlert(scanRef) {
    const [msg, setMsg]     = useState('');
    const [flash, setFlash] = useState(false);
    const timer = useRef(null);
    useEffect(() => { preloadSounds(); return () => clearTimeout(timer.current); }, []);

    const raise = useCallback((text) => {
        playError();
        setFlash(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setFlash(false), 600);
        setMsg(text);
        scanRef.current?.blur();          // hides the keyboard, like the Picking Portal
    }, [scanRef]);

    const dismiss = useCallback(() => { setMsg(''); }, []);

    const node = (
        <>
            {flash && ReactDOM.createPortal(<div className="pkp-flash pkp-flash--red" />, document.body)}
            {msg && ReactDOM.createPortal(
                <div className="pp-scan-toast pp-scan-toast--clickable" onClick={dismiss}>
                    <div className="pp-scan-toast__text">
                        ⛔ {msg}
                        <span className="pp-scan-toast__dismiss">Tap to dismiss</span>
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
    return { alertMsg: msg, raise, dismiss, alertNode: node };
}

// A wedge / Bluetooth scanner types the code then Enter. Collect it unless a field has the cursor (then the field
// gets the keystrokes, e.g. the scan box when tapped, or the Qty box). A pause of over 1.5 s drops a half-typed buffer.
export function useScannerCapture(onScan, active) {
    const buf  = useRef('');
    const last = useRef(0);
    const cb   = useRef(onScan);
    const chain = useRef(Promise.resolve());
    const on    = useRef(active);
    cb.current = onScan;
    on.current = active;
    useEffect(() => {
        if (!active) { buf.current = ''; return undefined; }
        const onKey = (e) => {
            const tag = document.activeElement?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            const now = Date.now();
            if (now - last.current > 1500) buf.current = '';
            last.current = now;
            if (e.key === 'Enter') {
                const v = buf.current.trim();
                buf.current = '';
                // one at a time, in order: a second scan made while the first is still being looked up is queued, not dropped
                if (v) chain.current = chain.current
                    .then(() => (on.current ? cb.current(v) : undefined))   // a notice went up meanwhile: the rest of the burst waits
                    .catch(() => {})
                    .then(() => new Promise(r => setTimeout(r, 80)));   // let the busy flag clear before the next one
            } else if (e.key && e.key.length === 1) {
                buf.current += e.key;
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [active]);
}
